import { FC } from 'react';
import classNames from 'classnames';
import { Container } from '@/components';
import { useIsMobile } from '@/hooks';

import style from './hero.module.scss';

interface HeroCarouselSkeletonProps {
  variation?:
    | 'carouselSingleImagePerView'
    | 'carouselThreeImagesPerView'
    | 'carouselFourImagesPerView';
  fullWidth?: boolean;
  withousVerticalSpace?: boolean;
  bottomSpacing?: boolean;
  background?: string;
}

const HeroCarouselSkeleton: FC<HeroCarouselSkeletonProps> = ({
  variation = 'carouselSingleImagePerView',
  fullWidth = true,
  withousVerticalSpace = false,
  bottomSpacing = false,
  background = '',
}) => {
  const { isMobile } = useIsMobile();

  const carouselDefault = variation === 'carouselSingleImagePerView';
  const carousel3Images = variation === 'carouselThreeImagesPerView';
  const carousel4Images = variation === 'carouselFourImagesPerView';

  const getMaxHeight = () => {
    const paddingTop = isMobile ? '58%' : '33.33%';
    return paddingTop;
  };

  const renderSkeletonImage = (secondary = false, key: string) => {
    return (
      <div
        className={classNames({
          [style.banner__secondary]: secondary,
          [style.banner__secondary_v]: carousel3Images && secondary,
          [style.banner__secondary_h]: carousel4Images && secondary,
        })}
        key={key}
      >
        <figure
          className={classNames(style.banner__figure_skeleton, {
            [style.banner__figure_simple]:
              (carouselDefault || carousel4Images) && !secondary,
            [style.banner__figure]: carousel3Images && !secondary,
            [style.banner__secondary_v]: carousel3Images && secondary,
            [style.banner__secondary_h]: carousel4Images && secondary,
          })}
          style={{ paddingTop: getMaxHeight() }}
        />
        {!secondary && (
          <figure
            className={classNames(
              style.banner__figure_mobile,
              style.banner__figure_skeleton,
            )}
            style={{ paddingTop: getMaxHeight() }}
          />
        )}
      </div>
    );
  };

  return (
    <section
      className={classNames(style.hero, style.hero__skeleton, {
        [style.hero__withous_margin]: carouselDefault && withousVerticalSpace,
        [style.hero__with_margin]: !withousVerticalSpace,
        [style.hero__spacing_bottom]: bottomSpacing,
        [style.hero__background_image]: carousel4Images || carousel3Images,
      })}
      style={{ background }}
    >
      <Container
        className={classNames(style.container__clear, {
          [style.container__grid]: !carouselDefault,
          [style.container__grid_tree_columns]: carousel4Images,
          [style.container__full_width]: fullWidth,
        })}
      >
        <div
          className={classNames({
            [style.banner__main_full]: carouselDefault,
            [style.banner__main]: carousel3Images,
            [style.banner__main_four_columns]: carousel4Images,
          })}
        >
          {renderSkeletonImage(false, 'banner_main_skeleton')}
        </div>

        {!carouselDefault && (
          <>
            {carousel4Images &&
              renderSkeletonImage(true, 'banner_three_skeleton')}
            {renderSkeletonImage(true, 'banner_one_skeleton')}
            {renderSkeletonImage(true, 'banner_two_skeleton')}
          </>
        )}
      </Container>
    </section>
  );
};

export default HeroCarouselSkeleton;
